import React from 'react'

import {useProductList} from '../util/product-list-context'
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Heading,
    Box,
    Text,
    Grid
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronDownIcon, ChevronUpIcon} from '../../../components/custom-icons'

/**
 * FaqList is a React functional component that renders a list of frequently asked questions (FAQs)
 * for a specific product category. It utilizes the `useProductList` hook to retrieve the category
 * data, which includes the FAQ title and up to five FAQ items, each consisting of a question and
 * an answer.
 *
 * @returns {JSX.Element|null} Returns a JSX element containing the FAQ list if there is a title and at least one question-answer pair; otherwise, returns null.
 */
const FaqList = () => {
    const {category} = useProductList()

    const faqData = {
        title: category?.c_faqTitle || '',
        items: [
            {
                question: category?.c_faqQuestion1,
                answer: category?.c_faqAnswer1
            },
            {
                question: category?.c_faqQuestion2,
                answer: category?.c_faqAnswer2
            },
            {
                question: category?.c_faqQuestion3,
                answer: category?.c_faqAnswer3
            },
            {
                question: category?.c_faqQuestion4,
                answer: category?.c_faqAnswer4
            },
            {
                question: category?.c_faqQuestion5,
                answer: category?.c_faqAnswer5
            }
        ].filter((item) => item.question && item.answer)
    }

    if (!faqData.title || faqData.items.length === 0) {
        return null
    }

    return (
        <Grid templateColumns={{base: '1fr', lg: '1fr 1fr'}} gap={{base: 45, lg: 60}}>
            {/* FAQ Title */}
            <Heading as="h2" fontSize="3xl" textTransform="uppercase">
                {faqData.title}
            </Heading>

            {/* FAQ Accordion */}
            <Box>
                <Accordion
                    allowToggle={true}
                    reduceMotion={true}
                    borderTop="1px solid"
                    borderColor="borderGray"
                >
                    {faqData.items.map((item, index) => (
                        <AccordionItem
                            key={index}
                            borderTop="none"
                            borderBottom="1px solid"
                            borderColor="borderGray"
                        >
                            {({isExpanded}) => (
                                <>
                                    <Heading as="h3">
                                        <AccordionButton
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            py={12}
                                            paddingStart={0}
                                            paddingEnd={6}
                                            gap={10}
                                        >
                                            <Text as="span" align="left" textStyle="eyeBrowSmall">
                                                {item.question}
                                            </Text>
                                            {isExpanded ? (
                                                <ChevronUpIcon boxSize="12px" />
                                            ) : (
                                                <ChevronDownIcon boxSize="12px" />
                                            )}
                                        </AccordionButton>
                                    </Heading>

                                    <AccordionPanel px={0} paddingTop={0} paddingBottom={12}>
                                        <Text align="left" textStyle="bodySmall" color="darkGray">
                                            {item.answer}
                                        </Text>
                                    </AccordionPanel>
                                </>
                            )}
                        </AccordionItem>
                    ))}
                </Accordion>
            </Box>
        </Grid>
    )
}

FaqList.propTypes = {}

export default FaqList
